import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useQuery } from "@apollo/client";
import {
  DEFAULT_PAGINATION_SCROLL_LIMIT,
  MIN_SEARCH_TERM_LENGTH,
} from "../components/common/Constants";

// welcome to infinite scroll hook //

// this hook is used to fetch data in chunks and display it in a list //
// when the user scrolls to the bottom of the list, the hook fetches more data //
// this currently uses cache-and-network fetch policy to fetch data //
// that means it will first fetch data from the cache and then fetch from the network (every time) //

// note: if a mutation returns back the data that was mutated, apollo will update the cache //
// and we don't have to call a refetch, saving on load times and network calls //

interface InitialVariables {
  first?: number | null; // should normally be set to DEFAULT_PAGINATION_SIZE unless you are trying to query all data //
  [key: string]: any;
}

interface UseInfiniteScrollProps {
  query: any;
  initialVariables: InitialVariables;
  refetchIndex?: number;
  skip?: boolean;
  extractData: (data: any) => any[];
  filterFunction?: (item: any) => boolean;
  requiredSearchTermVariableName?: string;
}

// note: in this iteration initialVariables cannot be empty

const useInfiniteScroll = ({
  query,
  initialVariables,
  refetchIndex,
  skip,
  filterFunction,
  extractData,
  requiredSearchTermVariableName,
}: UseInfiniteScrollProps) => {
  const [dataMap, setDataMap] = useState(new Map());
  const [isInfiniteScrollFetching, setIsInfiniteScrollFetching] =
    useState(false);
  const [shouldShowLoad, setShouldShowLoad] = useState(true);
  const [variables, setVariables] = useState(initialVariables);

  // if the user is scrolled down, we want to preserve how far they scrolled when refetching //
  const firstVariable =
    dataMap?.size > (initialVariables.first ?? 0)
      ? dataMap?.size
      : initialVariables.first;

  const hasNoVariables = Object.keys(variables).length === 0;

  // Optional guard for required search term variable
  let hasValidSearchTerm = true;
  if (requiredSearchTermVariableName) {
    hasValidSearchTerm =
      variables?.[requiredSearchTermVariableName]?.length >=
      MIN_SEARCH_TERM_LENGTH;
  }

  const { data, loading, refetch, fetchMore, error } = useQuery(query, {
    variables: variables,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    skip: hasNoVariables || skip || !hasValidSearchTerm,
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!loading && data) {
      setIsInfiniteScrollFetching(false);
      setShouldShowLoad(false);
    }
  }, [loading, data]);

  // allows for dynamic keys in the data object example: data.addresses.edges //
  const firstKey = useMemo(() => (data ? Object.keys(data)[0] : null), [data]);
  const pageInfo = useMemo(
    () => (firstKey ? data[firstKey]?.pageInfo : null),
    [data, firstKey],
  );

  /** Prevents overlapping load-more while React state is still catching up */
  const loadMoreInFlightRef = useRef(false);

  // sets the data we get back from the query to the dataMap //
  // we use a map here to prevent duplicates (since both cache and network will try to edit data) //
  useEffect(() => {
    setDataMap((prevDataMap) => {
      const newDataMap = new Map(isInfiniteScrollFetching ? prevDataMap : []);
      const extractedData = data ? (extractData(data) ?? []) : [];
      extractedData.forEach((item) => {
        // filter function is used to filter out data based on some criteria //
        // we use this as our "optimistic rendering" so that when we call a mutation we can //
        // filter those items out without having to call the network //
        if (filterFunction ? filterFunction(item) : true) {
          newDataMap.set(item.id, item);
        } else {
          newDataMap.delete(item.id);
        }
      });
      return newDataMap;
    });
    if (data) {
      setShouldShowLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, JSON.stringify(variables)]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      const target = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = target;
      const isNearBottom =
        (scrollTop + clientHeight) / scrollHeight >=
        DEFAULT_PAGINATION_SCROLL_LIMIT;

      if (
        !isNearBottom ||
        !pageInfo?.hasNextPage ||
        !pageInfo?.endCursor ||
        loading ||
        isInfiniteScrollFetching ||
        loadMoreInFlightRef.current
      ) {
        return;
      }

      loadMoreInFlightRef.current = true;
      setIsInfiniteScrollFetching(true);

      // IMPORTANT: use fetchMore (not refetch) so we don't "stick" the query variables to `after`.
      // If `refetch` is called with `after`, Apollo will treat that as the new active variables,
      // and later refetches (e.g. after delete) will unexpectedly include that cursor.
      void (async () => {
        try {
          await fetchMore({
            variables: {
              ...initialVariables,
              first: initialVariables.first,
              after: pageInfo.endCursor,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prev;

              const key =
                (fetchMoreResult ? Object.keys(fetchMoreResult)[0] : null) ??
                (prev ? Object.keys(prev)[0] : null);
              if (!key) return prev;

              const prevConn = prev?.[key];
              const moreConn = fetchMoreResult?.[key];
              if (!prevConn?.edges || !moreConn?.edges) return fetchMoreResult;

              return {
                ...prev,
                [key]: {
                  ...prevConn,
                  edges: [...prevConn.edges, ...moreConn.edges],
                  pageInfo: moreConn.pageInfo ?? prevConn.pageInfo,
                },
              };
            },
          });
        } catch (err) {
          console.error("Error fetching more data:", err);
        } finally {
          loadMoreInFlightRef.current = false;
          setIsInfiniteScrollFetching(false);
        }
      })();
    },
    [
      pageInfo?.hasNextPage,
      pageInfo?.endCursor,
      loading,
      isInfiniteScrollFetching,
      initialVariables,
      fetchMore,
    ],
  );

  // used to reset the dataMap and variables //
  // do not nescessarily want to show loading screen if we have data in cache that is immediately available //
  // effectively used for changing tabs or resetting the data
  useEffect(() => {
    setShouldShowLoad(true);
    setVariables(initialVariables);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialVariables)]);

  // refetch what is already on screen essentially (updated/delete/modified data) //
  // want to show loading screen
  useEffect(() => {
    if (refetchIndex) {
      setShouldShowLoad(true);
      !skip && refetch({ ...initialVariables, first: firstVariable });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchIndex]);

  useEffect(() => {
    if (error) {
      console.error("Error fetching data:", error);
    }
  }, [error]);

  const dataArray = Array.from(dataMap.values());

  // essentially a way to NOT show loading if we are fetching more data from infinite scroll else it's fair game //
  // the shouldShowLoad also stops the loading at the first moment the cache data is picked up //
  // so we don't have to wait for the network when cached data is available //
  const displayLoading = loading && shouldShowLoad && !isInfiniteScrollFetching;

  return {
    dataArray,
    loading,
    isInfiniteScrollFetching,
    displayLoading,
    refetch,
    handleScroll,
  };
};

export default useInfiniteScroll;
